import * as api from "@/api/article";
import { Toast } from "@/components/ui/toast";
import { authService } from "@/services/AuthService";
import { i18n } from "@/utils/i18n";

/** up-data 传入的点赞配置 */
export interface Data {
  articleId: number;
  isLoggedIn: boolean;
  liked: boolean;
  likeCount: number;
}

/**
 * Like —— 文章点赞 UI 组件
 *
 * 职责：监听点击事件、调用点赞接口、渲染 UI 状态。
 *
 * 未登录时：弹出登录 Modal，登录成功后以 set 语义执行点赞。
 */
export class Like {
  private liked: boolean;
  private likeCount: number;
  private liking = false;

  private btn: HTMLButtonElement;
  private iconSvg: SVGElement | null;
  private countEl: HTMLElement | null;

  private handleClick = () => this.toggle();

  constructor(
    element: HTMLElement,
    private data: Data,
  ) {
    this.liked = data.liked;
    this.likeCount = data.likeCount;

    this.btn = element as HTMLButtonElement;
    this.iconSvg = element.querySelector("svg");
    this.countEl = element.querySelector("[data-like-count]");

    this.init();
  }

  private init(): void {
    this.btn.addEventListener("click", this.handleClick);
    this.render();
  }

  /**
   * 点赞 toggle
   */
  private async toggle(): Promise<void> {
    if (this.liking) return;

    if (!authService.isSignedIn) {
      authService.signin({
        onAccepted: async () => {
          // 登录后用 set 语义，确保点赞成功
          await this.perform("set");
        },
        onDismissed: () => {
          // 取消登录，不做操作
        },
      });
      return;
    }

    // 已登录用户正常 toggle
    await this.perform("toggle");
  }

  /**
   * 执行点赞网络请求
   */
  private async perform(action: "set" | "toggle"): Promise<void> {
    this.liking = true;
    this.setLoading(true);

    try {
      const res = await api.like(this.data.articleId, action);
      this.liked = res.liked;
      this.likeCount = res.likeCount;
      this.render();
    } catch (_err) {
      Toast.error(i18n.t("article.like_failed"));
    } finally {
      this.liking = false;
      this.setLoading(false);
    }
  }

  private setLoading(loading: boolean): void {
    this.btn.disabled = loading;
    this.btn.classList.toggle("opacity-50", loading);
    this.btn.classList.toggle("cursor-not-allowed", loading);
  }

  /**
   * 根据当前状态更新 DOM
   */
  private render(): void {
    /* 计数文本 */
    if (this.countEl) {
      this.countEl.textContent = String(this.likeCount);
    }

    /* 按钮样式 */
    this.btn.classList.toggle("text-primary", this.liked);
    this.btn.classList.toggle(
      "text-muted",
      !this.liked && !this.btn.classList.contains("text-muted-light"),
    );
    this.btn.classList.toggle(
      "text-muted-light",
      !this.liked && this.btn.dataset.mutedClass === "muted-light",
    );

    /* 图标样式 */
    if (this.iconSvg) {
      this.iconSvg.classList.toggle("text-primary", this.liked);
      this.iconSvg.classList.toggle("fill-current", this.liked);
      this.iconSvg.classList.toggle("scale-110", this.liked);
    }

    /* disabled 状态 */
    // this.btn.classList.toggle('opacity-50', this.liking);
    this.btn.classList.toggle("cursor-not-allowed", this.liking);
  }

  destroy(): void {
    this.btn.removeEventListener("click", this.handleClick);
  }

  /** 注册 Unpoly 编译器 */
  static compiler(selector: string): void {
    up.compiler(selector, (element: Element, data: Data) => {
      const like = new Like(element as HTMLElement, data);
      return () => like.destroy();
    });
  }
}
